
import { Auth } from "./auth.js";
import { escapeHtml } from "./utils.js";
// ===== 组织架构管理模块 =====

let allDepartments = [];
let allPositions = [];
let allUsers = [];
let selectedDeptId = null;
let editingDeptId = null;

// ===== 初始化 =====
function initOrganization() {
    loadOrganizationData();
}

// ===== API请求基础 =====
async function orgFetch(url, options = {}) {
    const token = Auth.getToken();
    const defaults = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
        }
    };
    const response = await fetch(url, { ...defaults, ...options });
    if (response.status === 401) {
        Auth.logout();
        return null;
    }
    return response;
}

// ===== 加载组织架构数据 =====
async function loadOrganizationData() {
    try {
        const resp = await orgFetch('/api/organization');
        if (!resp) return;
        const data = await resp.json();
        allDepartments = data.departments || [];
        allPositions = data.positions || [];
        renderOrgTree();
    } catch (err) {
        console.error('加载组织架构失败:', err);
    }
}

// ===== 加载用户列表 =====
async function loadUsersForOrg() {
    try {
        const resp = await orgFetch('/api/users');
        if (!resp) return;
        const data = await resp.json();
        allUsers = data.users || [];
    } catch (err) {
        console.error('加载用户列表失败:', err);
    }
}

// ===== 渲染组织树 =====
function renderOrgTree() {
    const tree = document.getElementById('org-tree');
    if (!tree) return;
    
    const topDepts = allDepartments.filter(d => !d.parentId);
    
    if (topDepts.length === 0) {
        tree.innerHTML = '<div style="text-align:center;color:var(--text-muted);padding:20px;">暂无部门，请点击"新增部门"创建</div>';
        return;
    }
    
    tree.innerHTML = topDepts.map(dept => renderDeptNode(dept)).join('');
}

function renderDeptNode(dept) {
    const children = allDepartments.filter(d => d.parentId === dept.id);
    const manager = allUsers.find(u => u.username === dept.manager);
    const members = allPositions.filter(p => p.departmentId === dept.id);
    
    let html = `
        <div class="org-node ${selectedDeptId === dept.id ? 'selected' : ''}" data-id="${dept.id}">
            <div class="org-node-header" onclick="selectDepartment('${dept.id}')">
                <div class="org-node-info">
                    <i class="fa-solid fa-building"></i>
                    <span class="org-node-name">${escapeHtml(dept.name)}</span>
                    <span class="org-node-count">${members.length}人</span>
                </div>
                <div class="org-node-actions">
                    <button class="btn-sm btn-edit" onclick="event.stopPropagation();editDepartment('${dept.id}')" title="编辑">
                        <i class="fa-solid fa-pen"></i>
                    </button>
                    <button class="btn-sm btn-delete" onclick="event.stopPropagation();deleteDepartment('${dept.id}')" title="删除">
                        <i class="fa-solid fa-trash"></i>
                    </button>
                </div>
            </div>
    `;
    
    if (children.length > 0) {
        html += '<div class="org-children">';
        html += children.map(child => renderDeptNode(child)).join('');
        html += '</div>';
    }
    
    html += '</div>';
    return html;
}

// ===== 选择部门 =====
async function selectDepartment(deptId) {
    selectedDeptId = deptId;
    renderOrgTree();
    await loadUsersForOrg();
    renderDeptDetail(deptId);
}

// ===== 渲染部门详情 =====
function renderDeptDetail(deptId) {
    const detail = document.getElementById('org-detail');
    if (!detail) return;
    
    const dept = allDepartments.find(d => d.id === deptId);
    if (!dept) return;
    
    const manager = allUsers.find(u => u.username === dept.manager);
    const members = allPositions.filter(p => p.departmentId === deptId);
    const parentDept = dept.parentId ? allDepartments.find(d => d.id === dept.parentId) : null;
    
    let membersHtml = '';
    if (members.length > 0) {
        membersHtml = members.map(m => {
            const user = allUsers.find(u => u.username === m.username);
            return `
                <div class="member-item">
                    <div class="member-info">
                        <i class="fa-solid fa-user"></i>
                        <span>${escapeHtml(user ? user.displayName : m.username)}</span>
                        <span class="member-role">${escapeHtml(user ? user.title : '')}</span>
                    </div>
                    <button class="btn-sm btn-delete" onclick="removeMemberFromDept('${deptId}','${m.username}')">
                        <i class="fa-solid fa-times"></i>
                    </button>
                </div>
            `;
        }).join('');
    } else {
        membersHtml = '<div style="color:var(--text-muted);padding:12px;">暂无成员</div>';
    }
    
    detail.innerHTML = `
        <div class="dept-detail-header">
            <h3>${escapeHtml(dept.name)}</h3>
            <button class="btn-action" onclick="showMemberForm('${deptId}')"><i class="fa-solid fa-user-plus"></i> 管理成员</button>
        </div>
        <div class="dept-detail-info">
            <div class="info-item">
                <span class="info-label">上级部门:</span>
                <span class="info-value">${parentDept ? escapeHtml(parentDept.name) : '无'}</span>
            </div>
            <div class="info-item">
                <span class="info-label">部门经理:</span>
                <span class="info-value">${manager ? escapeHtml(manager.displayName) : '未设置'}</span>
            </div>
            <div class="info-item">
                <span class="info-label">成员数量:</span>
                <span class="info-value">${members.length} 人</span>
            </div>
        </div>
        <div class="dept-members">
            <h4>部门成员</h4>
            ${membersHtml}
        </div>
    `;
}

// ===== 显示部门表单 =====
async function showDepartmentForm(deptId = null) {
    editingDeptId = deptId;
    await loadUsersForOrg();
    
    const modal = document.getElementById('dept-form-modal');
    const title = document.getElementById('dept-form-title');
    const errEl = document.getElementById('dept-form-error');
    
    if (!modal) return;
    
    // 填充上级部门下拉
    const parentSelect = document.getElementById('df-parent');
    parentSelect.innerHTML = '<option value="">无（顶级部门）</option>';
    allDepartments.forEach(d => {
        if (d.id !== deptId) {
            parentSelect.innerHTML += `<option value="${d.id}">${escapeHtml(d.name)}</option>`;
        }
    });
    
    // 填充经理下拉
    const managerSelect = document.getElementById('df-manager');
    managerSelect.innerHTML = '<option value="">请选择</option>';
    allUsers.forEach(u => {
        managerSelect.innerHTML += `<option value="${u.username}">${escapeHtml(u.displayName)} (${escapeHtml(u.username)})</option>`;
    });
    
    if (deptId) {
        const dept = allDepartments.find(d => d.id === deptId);
        if (dept) {
            title.innerHTML = '<i class="fa-solid fa-building"></i> 编辑部门';
            document.getElementById('df-name').value = dept.name;
            document.getElementById('df-parent').value = dept.parentId || '';
            document.getElementById('df-manager').value = dept.manager || '';
        }
    } else {
        title.innerHTML = '<i class="fa-solid fa-building"></i> 新增部门';
        document.getElementById('df-name').value = '';
        document.getElementById('df-parent').value = '';
        document.getElementById('df-manager').value = '';
    }
    
    if (errEl) errEl.style.display = 'none';
    modal.style.display = 'flex';
}

// ===== 关闭部门表单 =====
function closeDepartmentForm() {
    const modal = document.getElementById('dept-form-modal');
    if (modal) modal.style.display = 'none';
    editingDeptId = null;
}

// ===== 提交部门表单 =====
async function submitDepartmentForm() {
    const name = document.getElementById('df-name').value.trim();
    const parentId = document.getElementById('df-parent').value;
    const manager = document.getElementById('df-manager').value;
    const errEl = document.getElementById('dept-form-error');
    
    if (!name) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = '部门名称不能为空'; }
        return;
    }
    
    const deptData = { name, parentId: parentId || null, manager: manager || null };
    
    try {
        let resp;
        if (editingDeptId) {
            resp = await orgFetch('/api/organization/departments/' + editingDeptId, {
                method: 'PUT',
                body: JSON.stringify(deptData)
            });
        } else {
            resp = await orgFetch('/api/organization/departments', {
                method: 'POST',
                body: JSON.stringify(deptData)
            });
        }
        
        if (resp && resp.ok) {
            closeDepartmentForm();
            await loadOrganizationData();
        } else {
            const err = await resp.json();
            if (errEl) { errEl.style.display = 'block'; errEl.textContent = err.error || '保存失败'; }
        }
    } catch (err) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = '网络错误: ' + err.message; }
    }
}

// ===== 编辑部门 =====
function editDepartment(deptId) {
    showDepartmentForm(deptId);
}

// ===== 删除部门 =====
async function deleteDepartment(deptId) {
    const dept = allDepartments.find(d => d.id === deptId);
    if (!dept) return;
    
    const children = allDepartments.filter(d => d.parentId === deptId);
    if (children.length > 0) {
        alert('该部门下有子部门，请先删除子部门');
        return;
    }
    
    if (!confirm('确定要删除部门 "' + dept.name + '" 吗？')) return;
    
    try {
        const resp = await orgFetch('/api/organization/departments/' + deptId, { method: 'DELETE' });
        if (resp && resp.ok) {
            if (selectedDeptId === deptId) {
                selectedDeptId = null;
                document.getElementById('org-detail').innerHTML = '<div style="text-align:center;color:var(--text-muted);padding:60px;"><i class="fa-solid fa-sitemap" style="font-size:3rem;margin-bottom:16px;display:block;"></i>请选择部门查看详情</div>';
            }
            await loadOrganizationData();
        }
    } catch (err) {
        console.error('删除部门失败:', err);
    }
}

// ===== 显示成员管理表单 =====
async function showMemberForm(deptId) {
    await loadUsersForOrg();
    
    const modal = document.getElementById('member-form-modal');
    const errEl = document.getElementById('member-form-error');
    if (!modal) return;
    
    const dept = allDepartments.find(d => d.id === deptId);
    document.getElementById('mf-dept').value = dept ? dept.name : '';
    document.getElementById('mf-dept').dataset.deptId = deptId;
    
    // 填充用户下拉
    const userSelect = document.getElementById('mf-user');
    userSelect.innerHTML = '<option value="">请选择用户</option>';
    allUsers.forEach(u => {
        userSelect.innerHTML += `<option value="${u.username}">${escapeHtml(u.displayName)} (${escapeHtml(u.username)})</option>`;
    });
    
    renderCurrentMembers(deptId);
    
    if (errEl) errEl.style.display = 'none';
    modal.style.display = 'flex';
}

// ===== 关闭成员表单 =====
function closeMemberForm() {
    const modal = document.getElementById('member-form-modal');
    if (modal) modal.style.display = 'none';
}

// ===== 渲染当前成员 =====
function renderCurrentMembers(deptId) {
    const container = document.getElementById('current-members');
    const members = allPositions.filter(p => p.departmentId === deptId);
    
    if (members.length === 0) {
        container.innerHTML = '<div style="color:var(--text-muted);padding:12px;">暂无成员</div>';
        return;
    }
    
    container.innerHTML = '<div style="font-weight:600;margin-bottom:8px;">当前成员:</div>' + 
        members.map(m => {
            const user = allUsers.find(u => u.username === m.username);
            return `
                <div class="member-item" style="padding:8px;background:var(--bg-card);border-radius:6px;margin-bottom:4px;display:flex;justify-content:space-between;align-items:center;">
                    <span>${escapeHtml(user ? user.displayName : m.username)}</span>
                    <button class="btn-sm btn-delete" onclick="removeMemberFromDept('${deptId}','${m.username}')">
                        <i class="fa-solid fa-times"></i>
                    </button>
                </div>
            `;
        }).join('');
}

// ===== 添加成员到部门 =====
async function addMemberToDept() {
    const deptId = document.getElementById('mf-dept').dataset.deptId;
    const username = document.getElementById('mf-user').value;
    const errEl = document.getElementById('member-form-error');
    
    if (!username) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = '请选择用户'; }
        return;
    }
    
    try {
        const resp = await orgFetch('/api/organization/members', {
            method: 'POST',
            body: JSON.stringify({ departmentId: deptId, username })
        });
        
        if (resp && resp.ok) {
            await loadOrganizationData();
            renderCurrentMembers(deptId);
            document.getElementById('mf-user').value = '';
            if (selectedDeptId === deptId) {
                renderDeptDetail(deptId);
            }
        } else {
            const err = await resp.json();
            if (errEl) { errEl.style.display = 'block'; errEl.textContent = err.error || '添加失败'; }
        }
    } catch (err) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = '网络错误: ' + err.message; }
    }
}

// ===== 从部门移除成员 =====
async function removeMemberFromDept(deptId, username) {
    if (!confirm('确定要将该成员移出部门吗？')) return;
    
    try {
        const resp = await orgFetch('/api/organization/members', {
            method: 'DELETE',
            body: JSON.stringify({ departmentId: deptId, username })
        });
        
        if (resp && resp.ok) {
            await loadOrganizationData();
            renderCurrentMembers(deptId);
            if (selectedDeptId === deptId) {
                renderDeptDetail(deptId);
            }
        }
    } catch (err) {
        console.error('移除成员失败:', err);
    }
}

// ===== 辅助函数 =====

// ===== DOMContentLoaded =====
document.addEventListener('DOMContentLoaded', function() {
    initOrganization();
});



// window bridges for template onclick handlers
window.showDepartmentForm = showDepartmentForm;
window.closeDepartmentForm = closeDepartmentForm;
window.submitDepartmentForm = submitDepartmentForm;
window.closeMemberForm = closeMemberForm;
window.addMemberToDept = addMemberToDept;

window.removeMemberFromDept = removeMemberFromDept;
window.selectDepartment = selectDepartment;
window.showMemberForm = showMemberForm;

window.editDepartment = editDepartment;
window.deleteDepartment = deleteDepartment;
export { initOrganization, loadOrganizationData };
